import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Loader2, RefreshCw, ExternalLink, ChevronDown, ChevronRight, Search, Archive,
  Send, Paperclip, MessageSquare, Zap, X, UserPlus, UserCheck, Pencil, Mail, ListChecks, Bot,
  History, Eye, Check,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { useRegion } from "@/contexts/RegionContext";
import { MirrorAutopilotButton, useMirrorAutopilotStatus, MirrorRowHint } from "@/pages/applications/MirrorAutopilot";
import { MirrorNeedsReviewButton } from "@/pages/applications/MirrorNeedsReview";
import { CodyButton } from "@/pages/applications/CodyBot";
import { ClosedDeals } from "@/pages/applications/MirrorClosed";
import { MirrorFilePreview } from "@/pages/applications/MirrorFilePreview";
import { MirrorFileChips } from "@/pages/applications/MirrorFileChips";
import { SumsubDigitalIdPanel } from "@/pages/applications/sumsub/SumsubDigitalIdPanel";
import { StageChips, IRSweepButton } from "@/pages/applications/MirrorStageChips";
import {
  mirrorBoard, mirrorItem, mirrorSetColumn, mirrorPostUpdate, mirrorInfoRequest, mirrorChase,
  mirrorBrokerSearch, mirrorSetBroker, mirrorBulkItemsStatus, mirrorBulkAssign,
  mirrorAssignItem, mirrorUploadFile, mirrorSendEmail, mirrorRenameItem, mirrorItemActivity, mirrorFileViewUrl,
} from "@/lib/api";

// ── column ids ───────────────────────────────────────────────────────────────
const C = {
  STATUS: "status",
  SF: "text_mkpn6xxt",
  DATE: "date4",
  AI: "color_mkt2tb5j",
  BD: "color_mkqexg9m",
  BROKER: "board_relation",
  COMMS: "numbers_mkmvk14v",
  EMAIL: "text_mkm837d5",
  CUSTOMER_EMAIL: "text_mkrsx2pw",
  CONTACT: "text_mkqecmwn",
  OB: "color",
  OFFER_AMT: "numeric",
  OFFER_SEND: "color_mkqh924z",
  OFFER_REJ: "color_mm12vds8",
  OFFER_FILE: "file_mkppktrq",
  DECL_REASON: "dropdown_mkm82kf4",
  DECL_SEND: "color_mkqhpvgy",
  REQ_INFO: "color_mkqxr56s",
  LAST_OFFER_CHASE: "date_mkzvg1gy",
  LAST_IR_CHASE: "date_mkzwt3jv",
  IR_TEXT: "long_text_mkzwf50s",
};

// Labels that fire live Make automations — always confirm first
const TRIGGERS = {
  [C.OFFER_SEND]: { labels: ["Send", "Offer Chase"], warn: "This fires the live offer automation — the offer email goes out to the broker/customer via Make." },
  [C.DECL_SEND]:  { labels: ["Send"], warn: "This fires the live decline automation — the decline email is sent and the app moves to Declined." },
  [C.REQ_INFO]:   { labels: ["Request", "Chase IR"], warn: "This fires the live info-request automation — an email goes out to the broker contact. 'Request' sends the latest IR: update; 'Chase IR' escalates the chase (3rd chase auto-declines)." },
};

const STATUS_TONE = {
  "Application": "bg-amber-100 text-amber-800 border-amber-300",
  "Processing": "bg-slate-200 text-slate-700 border-slate-300",
  "UW": "bg-blue-100 text-blue-800 border-blue-300",
  "UW - IR": "bg-blue-100 text-blue-800 border-blue-300",
  "TopUp - UW": "bg-blue-100 text-blue-800 border-blue-300",
  "Fast Track UW": "bg-blue-100 text-blue-800 border-blue-300",
  "UW - Conditional Offer": "bg-cyan-100 text-cyan-800 border-cyan-300",
  "Conditional Offer": "bg-cyan-100 text-cyan-800 border-cyan-300",
  "Info Requested": "bg-orange-100 text-orange-800 border-orange-300",
  "1st Chase -IR": "bg-orange-100 text-orange-800 border-orange-300",
  "2nd Chase -IR": "bg-red-100 text-red-700 border-red-300",
  "3rd Chase -IR": "bg-red-100 text-red-800 border-red-300",
  "1st Chase - Offer": "bg-orange-100 text-orange-800 border-orange-300",
  "2nd Chase - Offer": "bg-red-100 text-red-700 border-red-300",
  "3rd Chase - Offer": "bg-red-100 text-red-800 border-red-300",
  "Hold": "bg-purple-100 text-purple-800 border-purple-300",
  "Offer Accepted": "bg-green-100 text-green-800 border-green-300",
  "Raise Doc's": "bg-violet-100 text-violet-800 border-violet-300",
  "Doc's Out": "bg-violet-100 text-violet-800 border-violet-300",
  "Ready To Fund": "bg-emerald-100 text-emerald-800 border-emerald-300",
  "Funded": "bg-green-100 text-green-800 border-green-300",
  "Declined": "bg-red-100 text-red-700 border-red-300",
  "Declined - Pending": "bg-red-50 text-red-600 border-red-200",
};
const toneFor = (label) => STATUS_TONE[label] || "bg-slate-100 text-slate-600 border-slate-300";

const fmtDay = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
};
const daysSince = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return Math.floor((Date.now() - d.getTime()) / 86400000);
};

const curSym = () => (localStorage.getItem("sf_region") === "AUS" ? "A$" : "£");

const fmtMoney = (v) => {
  const n = Number(String(v || "").replace(/,/g, ""));
  return v && !Number.isNaN(n) ? `${curSym()}${n.toLocaleString("en-GB")}` : "";
};

// ── chase workbenches (mirror the InfoReqChase / OfferChase [UK] Make scenarios)
const CHASE_CONFIGS = {
  new_group1663: {                       // Information Requested
    kind: "ir",
    triggerCol: "color_mkqxr56s",
    triggerLabel: "Chase IR",
    lastCols: ["long_text_mkm88qgn", "date_mkzwt3jv"],
    stages: {
      "Info Requested": { next: "1st Chase -IR", what: "First chase email to the broker contact" },
      "1st Chase -IR":  { next: "2nd Chase -IR", what: "Second chase email to the broker contact" },
      "2nd Chase -IR":  { next: "3rd Chase -IR", what: "Final chase email to the broker contact" },
      "3rd Chase -IR":  { next: "Declined", what: "Internal escalation to Dean — the app is then DECLINED", danger: true },
    },
  },
  new_group: {                           // Conditional Offer
    kind: "offer",
    triggerCol: "color_mkqh924z",
    triggerLabel: "Offer Chase",
    lastCols: ["date_mkzvg1gy"],
    stages: {
      "Conditional Offer":  { next: "1st Chase - Offer", what: "Re-send the conditional offer (1st chase, offer file attached) to the broker contact" },
      "1st Chase - Offer":  { next: "2nd Chase - Offer", what: "Second offer chase email (offer file attached)" },
      "2nd Chase - Offer":  { next: "3rd Chase - Offer", what: "Final offer chase email (offer file attached)" },
      "3rd Chase - Offer":  { next: "Offer Rejected", what: "Final — the app is then marked OFFER REJECTED", danger: true },
    },
  },
};
const parseChaseDate = (raw) => {
  if (!raw) return null;
  let d = new Date(raw);
  if (!Number.isNaN(d.getTime())) return d;
  const m = String(raw).match(/\d{4}-\d{2}-\d{2}/);
  if (m) { d = new Date(m[0]); if (!Number.isNaN(d.getTime())) return d; }
  return null;
};
const daysSinceDate = (d) => (d ? Math.floor((Date.now() - d.getTime()) / 86400000) : null);
const chaseInfo = (item, thresholdDays, cfg) => {
  const cols = item.cols || {};
  const stage = cfg?.stages[cols[C.STATUS]];
  const lastDate = cfg?.lastCols.map(c => parseChaseDate(cols[c])).find(Boolean) || null;
  const days = daysSinceDate(lastDate);
  const chaseable = !!stage && cols[cfg?.triggerCol] !== cfg?.triggerLabel;
  const due = chaseable && (days == null || days >= thresholdDays);
  const isTopUp = (item.name || "").toUpperCase().includes("(TU)");
  return { stage, lastDate, days, chaseable, due, isTopUp };
};

function WhyBadges({ item, size = "sm" }) {
  const why = item.why || [];
  const cols = item.cols || {};
  if (!why.length) return size === "sm" ? <span className="text-slate-300">—</span> : null;
  const cls = size === "sm" ? "text-[9px]" : "text-[10px]";
  return (
    <span className="inline-flex items-center gap-1 flex-wrap">
      {why.includes("decline") && (
        <Badge variant="outline" className={cn(cls, "bg-red-100 text-red-700 border-red-300")}
          title={`Reason for decline: ${cols[C.DECL_REASON] || ""}`}>
          Decline ready
        </Badge>
      )}
      {why.includes("declined") && (
        <Badge variant="outline" className={cn(cls, "bg-slate-200 text-slate-700 border-slate-300")}
          title="Declined — still to be filed under Declined/Finished (Autopilot does this from Processing; in Information Requested use the group's 'File declined' button)" data-testid="mirror-why-declined-badge">
          Declined · to file
        </Badge>
      )}
      {why.includes("offer") && (
        <Badge variant="outline" className={cn(cls, "bg-cyan-100 text-cyan-800 border-cyan-300")}
          title={cols[C.OFFER_AMT] ? `Offer amount ${fmtMoney(cols[C.OFFER_AMT])}` : "Offer file on record"}>
          Offer {fmtMoney(cols[C.OFFER_AMT]) || "on file"}
        </Badge>
      )}
      {why.includes("ir") && (
        <Badge variant="outline" className={cn(cls, "bg-orange-100 text-orange-800 border-orange-300")}
          title={item.ir_snippet || "An IR: update is on this file"}>
          IR out
        </Badge>
      )}
    </span>
  );
}

// ── inline editors ───────────────────────────────────────────────────────────
function LabelSelect({ value, options, onCommit, testId, className, allowEmpty = false }) {
  const [confirming, setConfirming] = useState(null); // {label, warn}
  const [busy, setBusy] = useState(false);
  const commit = async (label) => {
    setBusy(true);
    try { await onCommit(label); } finally { setBusy(false); }
  };
  return (
    <>
      <select
        value={value || ""}
        disabled={busy}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => {
          const label = e.target.value;
          if (label === (value || "")) return;
          if (options.trigger && options.trigger.labels.includes(label)) {
            setConfirming({ label, warn: options.trigger.warn });
          } else commit(label);
        }}
        className={cn("h-6 text-[11px] border rounded px-1 bg-white cursor-pointer max-w-full truncate",
          busy && "opacity-50", className)}
        data-testid={testId}
      >
        {allowEmpty && !(options.list || []).includes(value || "") && (value || "") !== "" && (
          <option value={value}>{value}</option>
        )}
        {allowEmpty && <option value="">—</option>}
        {!allowEmpty && !(options.list || []).includes(value || "") && <option value={value || ""}>{value || "—"}</option>}
        {(options.groups && options.groups.length > 0)
          ? options.groups.map(g => (
              <optgroup key={g.label} label={g.label}>
                {g.options.map(o => <option key={o} value={o}>{o}</option>)}
              </optgroup>
            ))
          : (options.list || []).map(o => <option key={o} value={o}>{o}</option>)}
      </select>
      {confirming && (
        <AlertDialog open onOpenChange={(o) => !o && setConfirming(null)}>
          <AlertDialogContent data-testid="mirror-trigger-confirm">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-amber-500" />Set to "{confirming.label}"?
              </AlertDialogTitle>
              <AlertDialogDescription>{confirming.warn}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="mirror-trigger-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-amber-600 hover:bg-amber-700"
                onClick={() => { const l = confirming.label; setConfirming(null); commit(l); }}
                data-testid="mirror-trigger-confirm-btn">
                Yes — fire it
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </>
  );
}

function InlineInput({ value, onCommit, testId, className, placeholder, type = "text" }) {
  const [v, setV] = useState(value ?? "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setV(value ?? ""); }, [value]);
  const commit = async () => {
    if ((v ?? "") === (value ?? "")) return;
    setBusy(true);
    try { await onCommit(v); } finally { setBusy(false); }
  };
  return (
    <input
      type={type}
      value={v}
      placeholder={placeholder}
      disabled={busy}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => setV(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
      className={cn("h-6 text-[11px] border rounded px-1.5 bg-white w-full", busy && "opacity-50", className)}
      data-testid={testId}
    />
  );
}

// ── main page ────────────────────────────────────────────────────────────────
export default function MondayMirror() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [open, setOpen] = useState(() => new Set(["new_group64800", "new_group__1"]));
  const [query, setQuery] = useState("");
  const [drawerId, setDrawerId] = useState(() => new URLSearchParams(window.location.search).get("item") || null);   // ?item= deep link (Slack recipient-check lines)
  const [lastSync, setLastSync] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const drawerRef = useRef(null);
  drawerRef.current = drawerId;
  const { user } = useAuth();
  const { currentRegion } = useRegion();
  const me = user?.name || user?.email || "";
  const { status: apStatus, refresh: refreshApStatus } = useMirrorAutopilotStatus();
  const [codyOpen, setCodyOpen] = useState(false);
  const apQueued = useMemo(() => {
    const map = new Map();
    for (const q of apStatus?.queued_items || []) map.set(String(q.item_id), q);
    return map;
  }, [apStatus]);
  const apFlagged = useMemo(() => {
    const map = new Map();
    for (const f of apStatus?.flagged_items || []) map.set(String(f.item_id), f);
    return map;
  }, [apStatus]);
  const apParked = useMemo(() => {
    const map = new Map();
    for (const e of [...(apStatus?.open_events || [])].reverse()) map.set(String(e.item_id), e);  // newest wins
    return map;
  }, [apStatus]);

  const fetchBoard = useCallback(async (silent = false, fresh = false) => {
    try {
      if (!silent) setLoading(true);
      const r = await mirrorBoard(fresh);
      setData(r);
      setErr(null);
      setLastSync(r.fetched_at ? new Date(r.fetched_at) : new Date());
    } catch (e) {
      if (!silent) setErr(e.response?.data?.detail || e.message);
    } finally { if (!silent) setLoading(false); }
  }, []);

  useEffect(() => { fetchBoard(); }, [fetchBoard]);
  useEffect(() => {
    const t = setInterval(() => {
      if (document.visibilityState === "visible" && !drawerRef.current) fetchBoard(true);
    }, 90000);
    return () => clearInterval(t);
  }, [fetchBoard]);
  const handleApExecuted = useCallback(() => { fetchBoard(true); refreshApStatus(); }, [fetchBoard, refreshApStatus]);

  const [showClosed, setShowClosed] = useState(false);
  const options = data?.options || {};
  const optionGroups = data?.option_groups || {};
  const liveMatches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q || !data?.groups) return null;
    return data.groups.reduce((n, g) => n + g.items.filter(it =>
      (it.name || "").toLowerCase().includes(q) || (it.cols?.[C.SF] || "").toLowerCase().includes(q)
      || (it.cols?.[C.BROKER] || "").toLowerCase().includes(q) || (it.cols?.[C.CONTACT] || "").toLowerCase().includes(q)).length, 0);
  }, [data, query]);
  const groups = useMemo(() => {
    const gs = data?.groups || [];
    const q = query.trim().toLowerCase();
    if (!q) return gs;
    return gs.map(g => ({
      ...g,
      items: g.items.filter(it =>
        (it.name || "").toLowerCase().includes(q)
        || (it.cols?.[C.SF] || "").toLowerCase().includes(q)
        || (it.cols?.[C.BROKER] || "").toLowerCase().includes(q)
        || (it.cols?.[C.CONTACT] || "").toLowerCase().includes(q)),
    }));
  }, [data, query]);

  const setCol = async (itemId, columnId, value, label) => {
    await mirrorSetColumn(itemId, columnId, value);
    setData(prev => prev ? {
      ...prev,
      groups: prev.groups.map(g => ({
        ...g,
        items: g.items.map(it => it.id === itemId ? { ...it, cols: { ...it.cols, [columnId]: value } } : it),
      })),
    } : prev);
    toast.success(`${label || "Field"} updated`);
  };

  const wrapSet = (itemId, columnId, label) => async (value) => {
    try { await setCol(itemId, columnId, value, label); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const assignItem = async (itemId, name) => {
    try {
      await mirrorAssignItem(itemId, name);
      setData(prev => prev ? {
        ...prev,
        groups: prev.groups.map(g => ({
          ...g,
          items: g.items.map(it => it.id === itemId ? { ...it, assigned: name } : it),
        })),
      } : prev);
      toast.success(name ? "Assigned to you" : "Unassigned");
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const toggleSelect = (id) => setSelected(prev => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const toggleGroupSelect = (group) => setSelected(prev => {
    const n = new Set(prev);
    const ids = group.items.map(it => it.id);
    const all = ids.length > 0 && ids.every(id => n.has(id));
    ids.forEach(id => (all ? n.delete(id) : n.add(id)));
    return n;
  });

  return (
    <div className="space-y-3" data-testid="monday-mirror-page">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 flex-1 min-w-[240px]">
          <div className="relative flex-1 max-w-xs">
            <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search SF number, company, broker…"
              className="h-8 text-xs pl-7" data-testid="mirror-search-input" />
          </div>
          {data?.board_url && (
            <a href={data.board_url} target="_blank" rel="noreferrer"
              className="text-[11px] text-blue-600 hover:underline flex items-center gap-1" data-testid="mirror-board-link">
              <ExternalLink className="w-3 h-3" />Open board in Monday
            </a>
          )}
        </div>
        <div className="flex items-center gap-2">
          {lastSync && (
            <span className="text-[10px] text-muted-foreground" data-testid="mirror-last-sync">
              synced {lastSync.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
            </span>
          )}
          <Button size="sm" variant={showClosed ? "default" : "outline"} className="h-8 text-xs" onClick={() => setShowClosed(v => !v)} data-testid="mirror-closed-toggle">
            <Archive className="w-3.5 h-3.5 mr-1" /> Closed & funded
          </Button>
          {currentRegion && (
            <MirrorNeedsReviewButton
              count={apStatus?.needs_review_deals || 0}
              onOpenItem={(itemId) => setDrawerId(itemId)}
              onOpenCody={() => setCodyOpen(true)}
              onChanged={() => { refreshApStatus(); fetchBoard(true); }}
            />
          )}
          {currentRegion && <MirrorAutopilotButton onExecuted={handleApExecuted} status={apStatus} refreshStatus={refreshApStatus} />}
          {currentRegion && <CodyButton open={codyOpen} onOpenChange={setCodyOpen} />}
          <Button size="sm" variant="outline" onClick={() => fetchBoard(false, true)} disabled={loading} data-testid="mirror-refresh-btn">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </Button>
        </div>
      </div>

      {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3" data-testid="mirror-error">{err}</div>}
      {apStatus?.enabled && apStatus?.outward_off && (
        <div className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded p-2.5 flex items-center gap-2" data-testid="mirror-ap-outward-off-banner">
          <Bot className="w-3.5 h-3.5 text-amber-600 shrink-0" />
          Autopilot is on but <span className="font-semibold">outward firing is off</span> — Offer Send / Decline Send / Request Info need firing by hand. When it's switched back on, the first scan queues for approval instead of firing.
        </div>
      )}
      {(apStatus?.catch_up_pending || 0) > 0 && (
        <div className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded p-2.5 flex items-center gap-2" data-testid="mirror-ap-catchup-pending-banner">
          <Bot className="w-3.5 h-3.5 text-amber-600 shrink-0" />
          <span className="font-semibold">{apStatus.catch_up_pending} catch-up proposal(s)</span> are waiting in the Autopilot queue — nothing from them fires on its own. Open Autopilot → Queue to approve or dismiss them before firing anything by hand.
        </div>
      )}
      {loading && !data && (
        <div className="p-10 text-center text-sm text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />Loading the Funding Apps board…
        </div>
      )}

      {selected.size > 0 && (
        <BulkActionBar
          count={selected.size}
          me={me}
          statusOptions={options[C.STATUS] || []}
          statusGroups={(optionGroups || {})[C.STATUS]}
          onApply={async (label) => {
            const r = await mirrorBulkItemsStatus([...selected], label, me);
            toast.success(`Status "${label}": ${r.changed}/${r.total} item(s) updated${r.failed ? ` — ${r.failed} failed` : ""}`);
            setSelected(new Set());
            fetchBoard(true);
          }}
          onAssign={async (name) => {
            const ids = new Set(selected);
            const r = await mirrorBulkAssign([...ids], name);
            setData(prev => prev ? {
              ...prev,
              groups: prev.groups.map(g => ({
                ...g,
                items: g.items.map(it => ids.has(it.id) ? { ...it, assigned: name } : it),
              })),
            } : prev);
            toast.success(name ? `Assigned ${r.total} item(s) to ${name}` : `Unassigned ${r.total} item(s)`);
            setSelected(new Set());
          }}
          onClear={() => setSelected(new Set())}
        />
      )}

      {query.trim().length >= 2 && liveMatches === 0 && !showClosed && (
        <div className="text-xs text-slate-700 bg-slate-50 border rounded p-2.5 flex items-center gap-2" data-testid="mirror-no-live-match">
          <Archive className="w-3.5 h-3.5 text-slate-500 shrink-0" />
          Nothing live matches "{query.trim()}". <button type="button" className="text-violet-700 hover:underline font-medium" onClick={() => setShowClosed(true)} data-testid="mirror-search-closed-link">Search closed & funded deals</button>
        </div>
      )}
      {showClosed && <ClosedDeals query={query} onOpenItem={setDrawerId} boardUrl={data?.board_url} />}
      {groups.map(g => (
        <GroupSection
          key={g.id}
          group={g}
          options={options}
          optionGroups={optionGroups}
          isOpen={open.has(g.id) || !!query.trim()}
          onToggle={() => setOpen(prev => { const n = new Set(prev); n.has(g.id) ? n.delete(g.id) : n.add(g.id); return n; })}
          onOpenItem={setDrawerId}
          wrapSet={wrapSet}
          onAssign={assignItem}
          me={me}
          onRefresh={() => fetchBoard(true)}
          selected={selected}
          onToggleSelect={toggleSelect}
          onToggleGroupSelect={() => toggleGroupSelect(g)}
          apQueued={apQueued}
          apFlagged={apFlagged}
          apParked={apParked}
          apEnabled={apStatus?.enabled}
          onApExecuted={handleApExecuted}
        />
      ))}

      {drawerId && (
        <ItemDrawer
          itemId={drawerId}
          options={options}
          optionGroups={optionGroups}
          onClose={() => { setDrawerId(null); fetchBoard(true); }}
          wrapSet={wrapSet}
        />
      )}
    </div>
  );
}

// ── group section ────────────────────────────────────────────────────────────
function GroupSection({ group, options, optionGroups, isOpen, onToggle, onOpenItem, wrapSet, onAssign, me, onRefresh,
                        selected, onToggleSelect, onToggleGroupSelect, apQueued, apFlagged, apParked, apEnabled, onApExecuted }) {
  const optFor = (colId) => ({ list: options[colId] || [], trigger: TRIGGERS[colId], groups: (optionGroups || {})[colId] });
  const chaseCfg = CHASE_CONFIGS[group.id];
  const chaseMode = !!chaseCfg;
  const [threshold, setThreshold] = useState(3);
  const [chaseConfirm, setChaseConfirm] = useState(null);  // item
  const [chaseAllOpen, setChaseAllOpen] = useState(false);
  const [chasing, setChasing] = useState(() => new Set());
  const [reasonAsk, setReasonAsk] = useState(null);        // item blocked on OfferRejectedReason
  const [statusFilter, setStatusFilter] = useState(null);  // stage chip (IR / Conditional Offer) — John Garcia 20 Oct, ref 87cd02845f
  const rows = statusFilter ? group.items.filter(it => ((it.cols || {})[C.STATUS] || "(blank)") === statusFilter) : group.items;
  const declinedHere = chaseCfg?.kind === "ir" ? group.items.filter(it => (it.cols || {})[C.STATUS] === "Declined").length : 0;

  const dueItems = chaseMode
    ? rows.filter(it => chaseInfo(it, threshold, chaseCfg).due)
    : [];

  const fireChase = async (item, reason = null) => {
    setChasing(prev => new Set(prev).add(item.id));
    try {
      const r = await mirrorChase(item.id, chaseCfg.kind, reason);
      toast.success(`Chase fired for ${r.name || item.name} — ${r.from_status} → ${r.next_status}`);
      setTimeout(onRefresh, 8000);
      return true;
    } catch (e) {
      if (e.response?.status === 428) {
        // Final offer chase — an OfferRejectedReason must be set first
        setReasonAsk(item);
      } else {
        toast.error(e.response?.data?.detail || e.message);
      }
      return false;
    } finally {
      setChasing(prev => { const n = new Set(prev); n.delete(item.id); return n; });
    }
  };

  return (
    <Card className="overflow-hidden" data-testid={`mirror-group-${group.id}`}>
      <div className="w-full flex items-center gap-2 px-3 py-2 hover:bg-slate-50 transition">
        <input type="checkbox"
          checked={group.items.length > 0 && group.items.every(it => selected.has(it.id))}
          ref={el => { if (el) el.indeterminate = group.items.some(it => selected.has(it.id)) && !group.items.every(it => selected.has(it.id)); }}
          onChange={onToggleGroupSelect}
          onClick={(e) => e.stopPropagation()}
          disabled={group.items.length === 0}
          className="h-3.5 w-3.5 accent-blue-600 cursor-pointer shrink-0 disabled:opacity-30"
          title="Select the whole group"
          data-testid={`mirror-select-group-${group.id}`} />
        <button type="button" onClick={onToggle} className="flex items-center gap-2 flex-1 text-left"
          data-testid={`mirror-group-toggle-${group.id}`}>
          {isOpen ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
          <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: group.color || "#c4c4c4" }} />
          <span className="text-sm font-semibold">{group.title}</span>
          <Badge variant="outline" className="text-[10px]" data-testid={`mirror-group-count-${group.id}`}>
            {group.count}{group.has_more ? "+" : ""}
          </Badge>
          {group.items.filter(it => selected.has(it.id)).length > 0 && (
            <Badge className="text-[9px] h-4 px-1.5 bg-blue-600 hover:bg-blue-600" data-testid={`mirror-group-selcount-${group.id}`}>
              {group.items.filter(it => selected.has(it.id)).length} selected
            </Badge>
          )}
        </button>
        {chaseMode && (
          <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
            {chaseCfg.kind === "ir" && <IRSweepButton count={declinedHere} actor={me} onDone={onRefresh} />}
            <span className="text-[10px] text-muted-foreground">due after</span>
            <input type="number" min={0} max={30} value={threshold}
              onChange={(e) => setThreshold(Math.max(0, Number(e.target.value) || 0))}
              className="h-6 w-11 text-[11px] border rounded px-1 text-center"
              data-testid="mirror-chase-threshold-input" />
            <span className="text-[10px] text-muted-foreground">days</span>
            <Button size="sm" className="h-6 text-[10px] bg-orange-600 hover:bg-orange-700 px-2"
              disabled={dueItems.length === 0}
              onClick={() => setChaseAllOpen(true)} data-testid="mirror-chase-all-btn">
              <Zap className="w-3 h-3 mr-1" />Chase all due ({dueItems.length})
            </Button>
          </div>
        )}
      </div>
      {chaseMode && <StageChips group={group} cfg={chaseCfg} filter={statusFilter} onFilter={setStatusFilter} />}
      {isOpen && (
        <div className="border-t overflow-x-auto">
          {rows.length === 0 ? (
            <div className="p-4 text-center text-xs text-muted-foreground">{statusFilter ? `No items at "${statusFilter}".` : "No items in this group."}</div>
          ) : (
            <table className="w-full text-[11px]" data-testid={`mirror-group-table-${group.id}`}>
              <thead>
                <tr className="bg-slate-50 text-slate-500 text-left">
                  <th className="pl-3 pr-1 py-1.5 w-[28px]" />
                  <th className="px-2 py-1.5 font-medium min-w-[220px]">Funding application</th>
                  <th className="px-2 py-1.5 font-medium min-w-[80px]">Assigned</th>
                  <th className="px-2 py-1.5 font-medium">Date</th>
                  <th className="px-2 py-1.5 font-medium min-w-[150px]">Status</th>
                  {chaseMode && <th className="px-2 py-1.5 font-medium min-w-[95px]">Last chased</th>}
                  {chaseMode && <th className="px-2 py-1.5 font-medium min-w-[85px]">Chase</th>}
                  <th className="px-2 py-1.5 font-medium min-w-[90px]">Why</th>
                  <th className="px-2 py-1.5 font-medium min-w-[110px]">Offer Rej.</th>
                  <th className="px-2 py-1.5 font-medium">B/D</th>
                  <th className="px-2 py-1.5 font-medium min-w-[110px]">Broker</th>
                  <th className="px-2 py-1.5 font-medium w-[70px]">Comms %</th>
                  <th className="px-2 py-1.5 font-medium min-w-[150px]">Contact</th>
                  <th className="px-2 py-1.5 font-medium">OB</th>
                  <th className="px-2 py-1.5" />
                </tr>
              </thead>
              <tbody className="divide-y">
                {rows.map(it => {
                  const cols = it.cols || {};
                  const age = daysSince(cols[C.DATE]);
                  const ci = chaseMode ? chaseInfo(it, threshold, chaseCfg) : null;
                  return (
                    <tr key={it.id} className={cn("hover:bg-blue-50/40 cursor-pointer transition", selected.has(it.id) && "bg-blue-50/60")}
                      onClick={() => onOpenItem(it.id)} data-testid={`mirror-item-row-${it.id}`}>
                      <td className="pl-3 pr-1 py-1.5" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" checked={selected.has(it.id)} onChange={() => onToggleSelect(it.id)}
                          className="h-3.5 w-3.5 accent-blue-600 cursor-pointer block"
                          data-testid={`mirror-select-item-${it.id}`} />
                      </td>
                      <td className="px-2 py-1.5">
                        <div className="font-semibold text-slate-800 truncate max-w-[280px]">{it.name}</div>
                        <MirrorRowHint
                          queued={apQueued?.get(String(it.id))}
                          flagged={apFlagged?.get(String(it.id))}
                          parked={apParked?.get(String(it.id))}
                          why={it.why}
                          isProcessingGroup={group.id === "new_group__1"}
                          apEnabled={apEnabled}
                          onExecuted={onApExecuted}
                        />
                      </td>
                      <td className="px-2 py-1.5" onClick={(e) => e.stopPropagation()}>
                        {it.assigned ? (
                          <button type="button" onClick={() => onAssign(it.id, "")}
                            className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full border bg-sky-50 text-sky-700 border-sky-300 hover:bg-sky-100 transition"
                            title={`${it.assigned} — click to unassign`}
                            data-testid={`mirror-assigned-${it.id}`}>
                            <UserCheck className="w-2.5 h-2.5" />{it.assigned.split(" ")[0]}
                          </button>
                        ) : (
                          <button type="button" onClick={() => me && onAssign(it.id, me)}
                            className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full border border-dashed border-slate-300 text-slate-400 hover:text-sky-700 hover:border-sky-300 hover:bg-sky-50 transition"
                            title="Assign to me"
                            data-testid={`mirror-assign-me-${it.id}`}>
                            <UserPlus className="w-2.5 h-2.5" />me
                          </button>
                        )}
                      </td>
                      <td className="px-2 py-1.5 whitespace-nowrap text-slate-600">
                        {fmtDay(cols[C.DATE])}
                        {age != null && age >= 7 && (
                          <span className={cn("ml-1 text-[9px] font-semibold", age >= 14 ? "text-red-600" : "text-amber-600")}>
                            {age}d
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-1.5">
                        <LabelSelect value={cols[C.STATUS]} options={optFor(C.STATUS)}
                          onCommit={wrapSet(it.id, C.STATUS, "Status")}
                          className={cn("font-medium", toneFor(cols[C.STATUS]))}
                          testId={`mirror-status-${it.id}`} />
                      </td>
                      {chaseMode && (
                        <td className="px-2 py-1.5 whitespace-nowrap" data-testid={`mirror-lastchased-${it.id}`}>
                          {ci.lastDate ? (
                            <>
                              <span className="text-slate-600">{fmtDay(ci.lastDate.toISOString())}</span>
                              <span className={cn("ml-1 text-[9px] font-semibold",
                                ci.days >= threshold ? "text-red-600" : "text-slate-400")}>
                                {ci.days}d
                              </span>
                            </>
                          ) : (
                            <span className="text-[10px] text-amber-600 font-medium">never</span>
                          )}
                        </td>
                      )}
                      {chaseMode && (
                        <td className="px-2 py-1.5" onClick={(e) => e.stopPropagation()}>
                          {ci.chaseable ? (
                            <Button size="sm"
                              className={cn("h-6 text-[10px] px-2",
                                ci.stage?.danger ? "bg-red-600 hover:bg-red-700"
                                  : ci.due ? "bg-orange-600 hover:bg-orange-700" : "bg-slate-400 hover:bg-slate-500")}
                              disabled={chasing.has(it.id)}
                              title={ci.stage?.what}
                              onClick={() => setChaseConfirm(it)}
                              data-testid={`mirror-chase-btn-${it.id}`}>
                              {chasing.has(it.id) ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3 mr-0.5" />}
                              {ci.stage?.danger ? "Final" : "Chase"}
                            </Button>
                          ) : cols[chaseCfg.triggerCol] === chaseCfg.triggerLabel ? (
                            <span className="text-[9px] text-orange-600 font-medium">firing…</span>
                          ) : (
                            <span className="text-slate-300 text-[10px]">—</span>
                          )}
                        </td>
                      )}
                      <td className="px-2 py-1.5" data-testid={`mirror-why-${it.id}`}>
                        <WhyBadges item={it} />
                      </td>
                      <td className="px-2 py-1.5" onClick={(e) => e.stopPropagation()}>
                        <LabelSelect value={cols[C.OFFER_REJ]} options={optFor(C.OFFER_REJ)}
                          onCommit={wrapSet(it.id, C.OFFER_REJ, "Offer rejected reason")}
                          allowEmpty testId={`mirror-offerrej-${it.id}`} />
                      </td>
                      <td className="px-2 py-1.5">
                        <Badge variant="outline" className={cn("text-[9px]",
                          cols[C.BD] === "Direct" ? "bg-emerald-50 text-emerald-700 border-emerald-300" : "bg-purple-50 text-purple-700 border-purple-300")}>
                          {cols[C.BD] || "—"}
                        </Badge>
                      </td>
                      <td className="px-2 py-1.5 max-w-[160px]" onClick={(e) => e.stopPropagation()}>
                        <BrokerEdit value={cols[C.BROKER]} itemId={it.id} onChanged={onRefresh} />
                      </td>
                      <td className="px-2 py-1.5">
                        <InlineInput value={cols[C.COMMS]} onCommit={wrapSet(it.id, C.COMMS, "Comms %")}
                          className="w-14 text-center" testId={`mirror-comms-${it.id}`} />
                      </td>
                      <td className="px-2 py-1.5">
                        <div className="truncate max-w-[170px] text-slate-700">{cols[C.CONTACT] || "—"}</div>
                        <div className="truncate max-w-[170px] text-[10px] text-muted-foreground">{cols[C.EMAIL]}</div>
                      </td>
                      <td className="px-2 py-1.5">
                        <LabelSelect value={cols[C.OB]} options={optFor(C.OB)}
                          onCommit={wrapSet(it.id, C.OB, "OB")}
                          testId={`mirror-ob-${it.id}`} />
                      </td>
                      <td className="px-2 py-1.5 text-right">
                        <MessageSquare className="w-3.5 h-3.5 text-slate-300 inline" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
      {chaseConfirm && (
        <ChaseConfirmDialog item={chaseConfirm} cfg={chaseCfg} onClose={() => setChaseConfirm(null)}
          onConfirm={async () => { const it = chaseConfirm; setChaseConfirm(null); await fireChase(it); }} />
      )}
      {chaseAllOpen && (
        <ChaseAllDialog items={dueItems} threshold={threshold} cfg={chaseCfg} onClose={() => setChaseAllOpen(false)}
          fireChase={fireChase} onDone={() => { setChaseAllOpen(false); setTimeout(onRefresh, 8000); }} />
      )}
      {reasonAsk && (
        <FinalChaseReasonDialog item={reasonAsk} options={options[C.OFFER_REJ] || []}
          onClose={() => setReasonAsk(null)}
          onConfirm={async (reason) => { const it = reasonAsk; setReasonAsk(null); await fireChase(it, reason); }} />
      )}
    </Card>
  );
}

// Floating bulk-action bar — status + assign over the checkbox selection
function BulkActionBar({ count, me, statusOptions, statusGroups, onApply, onAssign, onClear }) {
  const [label, setLabel] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [assignName, setAssignName] = useState("");
  const [assignBusy, setAssignBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      await onApply(label);
      setLabel("");
      setConfirming(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const assign = async (name) => {
    setAssignBusy(true);
    try {
      await onAssign(name);
      setAssignName("");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setAssignBusy(false); }
  };

  return (
    <div className="sticky top-2 z-30 flex flex-wrap items-center gap-2 px-3 py-2 rounded-lg border border-blue-300 bg-blue-50/95 shadow-sm backdrop-blur"
      data-testid="mirror-bulkbar">
      <ListChecks className="w-4 h-4 text-blue-700" />
      <span className="text-xs font-semibold text-blue-900" data-testid="mirror-bulkbar-count">{count} selected</span>
      <span className="text-[11px] text-blue-800">set status to</span>
      <select value={label} onChange={(e) => { setLabel(e.target.value); setConfirming(false); }}
        className="h-7 text-[11px] border border-blue-300 rounded px-1.5 bg-white max-w-[190px]"
        data-testid="mirror-bulkbar-select">
        <option value="">Choose status…</option>
        {(statusGroups && statusGroups.length > 0)
          ? statusGroups.map(g => (
              <optgroup key={g.label} label={g.label}>
                {g.options.map(o => <option key={o} value={o}>{o}</option>)}
              </optgroup>
            ))
          : statusOptions.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
      {label && !confirming && (
        <Button size="sm" className="h-7 text-[11px] px-3 bg-blue-600 hover:bg-blue-700"
          onClick={() => setConfirming(true)} data-testid="mirror-bulkbar-apply">
          Apply to {count}
        </Button>
      )}
      {label && confirming && (
        <Button size="sm" className="h-7 text-[11px] px-3 bg-red-600 hover:bg-red-700" disabled={busy}
          onClick={run} data-testid="mirror-bulkbar-confirm">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : `Sure? Move ${count} → ${label}`}
        </Button>
      )}
      <span className="mx-1 h-4 w-px bg-blue-200" />
      <span className="text-[11px] text-blue-800">assign to</span>
      <Button size="sm" variant="outline" className="h-7 text-[11px] px-2 border-sky-300 text-sky-700 hover:bg-sky-50"
        disabled={assignBusy || !me} onClick={() => assign(me)} data-testid="mirror-bulkbar-assign-me">
        <UserPlus className="w-3 h-3 mr-1" />me
      </Button>
      <input value={assignName} onChange={(e) => setAssignName(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter" && assignName.trim()) assign(assignName.trim()); }}
        placeholder="or type a name…"
        className="h-7 text-[11px] border border-blue-300 rounded px-1.5 bg-white w-32"
        data-testid="mirror-bulkbar-assign-input" />
      {assignName.trim() && (
        <Button size="sm" className="h-7 text-[11px] px-2 bg-sky-600 hover:bg-sky-700"
          disabled={assignBusy} onClick={() => assign(assignName.trim())} data-testid="mirror-bulkbar-assign-go">
          {assignBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Assign"}
        </Button>
      )}
      <button type="button" onClick={() => assign("")} disabled={assignBusy}
        className="text-[11px] text-slate-500 hover:text-slate-700 hover:underline" data-testid="mirror-bulkbar-unassign">
        unassign
      </button>
      <button type="button" onClick={onClear}
        className="ml-auto text-[11px] text-blue-700 hover:underline" data-testid="mirror-bulkbar-clear">
        Clear selection
      </button>
    </div>
  );
}

function BrokerEdit({ value, itemId, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!editing || q.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await mirrorBrokerSearch(q);
        setResults(r.brokers || []);
      } catch { setResults([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [q, editing]);

  const pick = async (b) => {
    try {
      setBusy(true);
      await mirrorSetBroker(itemId, b.item_id, b.name);
      toast.success(`Broker linked: ${b.name}`);
      setEditing(false);
      setQ("");
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  if (!editing) {
    return (
      <button type="button" onClick={() => setEditing(true)} title="Change broker"
        className="flex items-center gap-1 text-left hover:text-blue-700 group max-w-full"
        data-testid="mirror-broker-edit-btn">
        <span className="truncate">{value || <span className="text-slate-400">— set broker —</span>}</span>
        <Pencil className="w-2.5 h-2.5 text-slate-300 group-hover:text-blue-600 shrink-0" />
      </button>
    );
  }
  return (
    <div className="relative">
      <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
        onKeyDown={(e) => e.key === "Escape" && setEditing(false)}
        placeholder="Search brokers…"
        className="w-full h-6 text-[11px] border rounded px-1.5"
        data-testid="mirror-broker-search-input" />
      {busy && <Loader2 className="absolute right-1 top-1 w-3.5 h-3.5 animate-spin text-slate-400" />}
      {results.length > 0 && (
        <div className="absolute z-50 mt-0.5 w-64 bg-white border rounded shadow-lg max-h-44 overflow-y-auto">
          {results.map((b) => (
            <button key={b.item_id} type="button" onClick={() => pick(b)}
              className="w-full text-left px-2 py-1 text-[11px] hover:bg-slate-50 border-b last:border-b-0"
              data-testid={`mirror-broker-opt-${b.item_id}`}>
              <span className="font-medium">{b.name}</span>
              {b.email && <span className="text-muted-foreground"> · {b.email}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── item drawer ──────────────────────────────────────────────────────────────
export function ItemDrawer({ itemId, options, optionGroups, onClose, wrapSet }) {
  const [item, setItem] = useState(null);
  const [err, setErr] = useState(null);
  const [note, setNote] = useState("");
  const [posting, setPosting] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [customReason, setCustomReason] = useState(null);   // null = closed, string = editing text
  const [uploadProgress, setUploadProgress] = useState("");
  const fileInputRef = useRef(null);
  const [irText, setIrText] = useState("");
  const [irSending, setIrSending] = useState(false);
  const [irConfirm, setIrConfirm] = useState(false);
  const [actionConfirm, setActionConfirm] = useState(null); // "offer" | "decline"
  const [actionBusy, setActionBusy] = useState(false);
  const [assignBusy, setAssignBusy] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [emailSending, setEmailSending] = useState(false);
  const [nameEdit, setNameEdit] = useState(null); // string while editing, else null
  const [nameSaving, setNameSaving] = useState(false);
  const [activityRows, setActivityRows] = useState(null); // null = not loaded yet
  const [activityLoading, setActivityLoading] = useState(false);
  const [updatesFilter, setUpdatesFilter] = useState("all"); // "all" | "team" | "system"
  const irSectionRef = useRef(null);
  const irTaRef = useRef(null);
  const { user } = useAuth();
  const me = user?.name || user?.email || "";

  const load = useCallback(async () => {
    try { setItem(await mirrorItem(itemId)); setErr(null); }
    catch (e) { setErr(e.response?.data?.detail || e.message); }
  }, [itemId]);
  useEffect(() => { load(); }, [load]);

  const loadActivity = async () => {
    if (activityRows !== null) return; // already loaded for this item
    setActivityLoading(true);
    try { setActivityRows((await mirrorItemActivity(itemId)).rows || []); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); setActivityRows([]); }
    finally { setActivityLoading(false); }
  };

  const saveName = async () => {
    const next = (nameEdit || "").trim();
    if (!next || next === item?.name) { setNameEdit(null); return; }
    setNameSaving(true);
    try {
      await mirrorRenameItem(itemId, next);
      setItem((prev) => prev ? { ...prev, name: next } : prev);
      setNameEdit(null);
      toast.success("Deal name updated");
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setNameSaving(false); }
  };

  const uploadFiles = async (list) => {
    const files = [...(list || [])];
    if (!files.length) return;
    setUploadingFiles(true);
    let ok = 0;
    const uploaded = [];
    for (const f of files) {
      if (f.size > 60 * 1024 * 1024) { toast.error(`${f.name} is over 60MB — skipped`); continue; }
      setUploadProgress(`${uploaded.length + 1}/${files.length} · ${f.name}`);
      try {
        const r = await mirrorUploadFile(itemId, f);
        if (r?.skipped?.length) toast.info(`${r.skipped.length === 1 && !r.unpacked ? f.name : `${r.skipped.length} file${r.skipped.length > 1 ? "s" : ""}`} already on the item — skipped`);
        if (r?.unpacked) {
          ok += r.unpacked.length; uploaded.push(...r.unpacked.map(n => `${n} (from ${f.name})`));
          if (r.failed?.length) toast.error(`${r.failed.length} file${r.failed.length > 1 ? "s" : ""} inside ${f.name} failed: ${r.failed.join(", ")}`);
        } else if (r?.uploaded !== false) { ok += 1; uploaded.push(f.name); }
      }
      catch (e) { toast.error(e.response?.data?.detail || `Upload failed: ${f.name}`); }
    }
    setUploadingFiles(false); setUploadProgress("");
    if (ok) {
      toast.success(`${ok} file${ok > 1 ? "s" : ""} uploaded to the Monday item`);
      // Leave a trace in the sales thread — who attached what, without opening the Files gallery
      try {
        await mirrorPostUpdate(itemId, `📎 ${ok} file${ok > 1 ? "s" : ""} uploaded via portal:\n${uploaded.map(n => `• ${n}`).join("\n")}`);
      } catch { /* upload already succeeded — don't fail the flow over the note */ }
      load();
    }
  };

  // Prefill the email-chase compose with the item's contact email + name
  useEffect(() => {
    if (!item) return;
    setEmailTo((prev) => prev || item.cols?.[C.EMAIL] || "");
    setEmailSubject((prev) => prev || `${item.name || ""} — update from SimplyFunded`);
  }, [item]);

  const sendChaseEmail = async () => {
    setEmailSending(true);
    try {
      await mirrorSendEmail(itemId, {
        to: emailTo.trim(), subject: emailSubject.trim(),
        body: emailBody.trim(), actor: me || "Ops",
      });
      toast.success(`Email sent to ${emailTo.trim()} from applications@ — logged on the Monday file`);
      setEmailBody("");
      setEmailOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Email send failed");
    } finally {
      setEmailSending(false);
    }
  };

  const cols = item?.cols || {};
  const optFor = (colId) => ({ list: options[colId] || [], trigger: TRIGGERS[colId], groups: (optionGroups || {})[colId] });
  const set = (colId, label) => async (value) => {
    try {
      await wrapSet(itemId, colId, label)(value);
      setItem(prev => prev ? { ...prev, cols: { ...prev.cols, [colId]: value } } : prev);
    } catch { /* toast already shown */ }
  };

  const postNote = async () => {
    if (!note.trim()) return;
    setPosting(true);
    try {
      await mirrorPostUpdate(itemId, note.trim());
      setNote("");
      toast.success("Update posted to Monday");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setPosting(false); }
  };

  const sendInfoRequest = async () => {
    setIrConfirm(false);
    setIrSending(true);
    try {
      await mirrorInfoRequest(itemId, irText.trim());
      setIrText("");
      toast.success("Info request fired — the automation emails the broker and sets status to Info Requested");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setIrSending(false); }
  };

  const offerReady = !!((cols[C.OFFER_AMT] || "").trim() || (cols[C.OFFER_FILE] || "").trim());
  const declineReady = !!(cols[C.DECL_REASON] || "").trim();
  const offerFired = cols[C.OFFER_SEND] === "Send";
  const declineFired = cols[C.DECL_SEND] === "Send";

  const fireAction = async () => {
    const type = actionConfirm;
    setActionConfirm(null);
    setActionBusy(true);
    try {
      if (type === "offer") {
        await set(C.OFFER_SEND, "Offer Send")("Send");
        toast.success("Offer Send fired — the automation emails the offer out");
      } else if (type === "decline") {
        await set(C.DECL_SEND, "Decline Send")("Send");
        toast.success("Decline Send fired — the portal sends the decline and moves the app to Declined. If the same decline already went out it is parked instead: the row shows why, with Approve / Dismiss.");
      }
    } finally { setActionBusy(false); }
  };

  const focusInfoRequest = () => {
    irSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    setTimeout(() => irTaRef.current?.focus(), 350);
  };

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-[880px] xl:max-w-[1150px] overflow-y-auto p-0" data-testid="mirror-item-drawer">
        <SheetHeader className="px-5 pt-5 pb-3 border-b sticky top-0 bg-white/95 backdrop-blur-sm z-20">
          <SheetTitle className="text-lg font-bold tracking-tight text-slate-900 flex items-center justify-between gap-2">
            {nameEdit !== null ? (
              <span className="flex items-center gap-1.5 flex-1 min-w-0">
                <Input value={nameEdit} onChange={(e) => setNameEdit(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") saveName(); if (e.key === "Escape") setNameEdit(null); }}
                  className="h-7 text-sm font-semibold" autoFocus data-testid="mirror-name-input" />
                <button type="button" onClick={saveName} disabled={nameSaving}
                  className="h-6 w-6 shrink-0 rounded flex items-center justify-center text-emerald-600 hover:bg-emerald-50"
                  title="Save" data-testid="mirror-name-save-btn">
                  {nameSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                </button>
                <button type="button" onClick={() => setNameEdit(null)} disabled={nameSaving}
                  className="h-6 w-6 shrink-0 rounded flex items-center justify-center text-slate-500 hover:bg-slate-100"
                  title="Cancel" data-testid="mirror-name-cancel-btn">
                  <X className="w-3.5 h-3.5" />
                </button>
              </span>
            ) : (
              <span className="truncate flex items-center gap-1.5 group flex-1 min-w-0" title={item?.name || ""}>
                <span className="truncate min-w-0">{item?.name || (err ? "Item not found" : "Loading…")}</span>
                {item && (
                  <button type="button" onClick={() => setNameEdit(item.name)}
                    className="h-5 w-5 shrink-0 rounded flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-700 opacity-60 hover:opacity-100 transition"
                    title="Fix the deal name" data-testid="mirror-name-edit-btn">
                    <Pencil className="w-3 h-3" />
                  </button>
                )}
              </span>
            )}
            <span className="flex items-center gap-2 shrink-0">
              {item?.url && (
                <a href={item.url} target="_blank" rel="noreferrer"
                  className="text-[11px] text-blue-600 hover:underline flex items-center gap-1 font-normal shrink-0"
                  data-testid="mirror-drawer-monday-link">
                  <ExternalLink className="w-3 h-3" />Monday
                </a>
              )}
              <button type="button" onClick={onClose}
                className="h-6 w-6 rounded flex items-center justify-center text-slate-500 hover:bg-slate-100 hover:text-slate-800 transition shrink-0"
                title="Close" data-testid="mirror-drawer-close-btn">
                <X className="w-4 h-4" />
              </button>
            </span>
          </SheetTitle>
          <div className="flex items-center gap-2 flex-wrap text-left" data-testid="mirror-drawer-chips">
            {cols[C.SF] && (
              <span className="text-[10px] font-mono font-semibold text-slate-400 bg-slate-100 rounded px-1.5 py-0.5">
                {/^SF-?/i.test(cols[C.SF]) ? cols[C.SF] : `SF-${cols[C.SF]}`}
              </span>
            )}
            {item?.group?.title && (
              <span className="text-[10px] font-medium text-slate-500 border border-slate-200 rounded-full px-2 py-0.5">
                {item.group.title}
              </span>
            )}
            {item && <WhyBadges item={item} size="sm" />}
          </div>
        </SheetHeader>

        {err && <div className="m-5 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">{err}</div>}
        {!item && !err && <div className="p-10 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>}

        {item?.archive_board && (
          <div className="mx-5 mt-3 text-[11px] text-violet-900 bg-violet-50 border border-violet-200 rounded p-2.5 flex items-start gap-2" data-testid="mirror-drawer-archived">
            <Archive className="w-3.5 h-3.5 mt-0.5 shrink-0 text-violet-600" />
            <span>
              This deal sits on the <span className="font-semibold">{item.archive_board}</span> board — Monday batch-archives Declined/Finished deals there.
              Notes, files and emails work as normal, but the chase / Request Info automations and the live group moves only run on the Funding Apps board:
              to work it again, move it back to Processing{item.url && <> <a href={item.url} target="_blank" rel="noreferrer" className="underline" data-testid="mirror-drawer-archived-link">in Monday</a></>} first.
            </span>
          </div>
        )}

        {item && (
          <div className="px-5 py-2.5 border-b bg-slate-50/80 flex flex-wrap items-center gap-2" data-testid="mirror-drawer-actions">
            <Button size="sm" className="h-7 text-[11px] bg-cyan-600 hover:bg-cyan-700"
              disabled={!offerReady || offerFired || actionBusy}
              title={offerFired ? "Offer Send is already set to Send" : (!offerReady ? "No offer amount or offer file on record yet" : `Fires the offer email (${fmtMoney(cols[C.OFFER_AMT]) || "file on record"})`)}
              onClick={() => setActionConfirm("offer")} data-testid="mirror-action-send-offer">
              <Send className="w-3 h-3 mr-1" />{offerFired ? "Offer sent" : `Send offer${fmtMoney(cols[C.OFFER_AMT]) ? ` ${fmtMoney(cols[C.OFFER_AMT])}` : ""}`}
            </Button>
            <Button size="sm" className="h-7 text-[11px] bg-red-600 hover:bg-red-700"
              disabled={!declineReady || declineFired || actionBusy}
              title={declineFired ? "Decline Send is already set to Send" : (!declineReady ? "Set a Reason for decline first (credit team usually pre-fills this)" : `Fires the decline email — reason: ${cols[C.DECL_REASON]}`)}
              onClick={() => setActionConfirm("decline")} data-testid="mirror-action-send-decline">
              <Send className="w-3 h-3 mr-1" />{declineFired ? "Decline sent" : "Send decline"}
            </Button>
            <Button size="sm" variant="outline" className="h-7 text-[11px] border-orange-300 text-orange-700 hover:bg-orange-50"
              onClick={focusInfoRequest} data-testid="mirror-action-request-info">
              <MessageSquare className="w-3 h-3 mr-1" />Request info
            </Button>
            <Button size="sm" variant="outline"
              className={cn("h-7 text-[11px] ml-auto",
                item.assigned ? "border-sky-300 text-sky-700 bg-sky-50 hover:bg-sky-100" : "text-slate-600")}
              disabled={assignBusy || (!item.assigned && !me)}
              title={item.assigned ? `${item.assigned} is working this — click to unassign` : "Tag this app as yours (portal-only)"}
              onClick={async () => {
                setAssignBusy(true);
                try {
                  const next = item.assigned ? "" : me;
                  await mirrorAssignItem(item.id, next);
                  setItem(prev => prev ? { ...prev, assigned: next } : prev);
                  toast.success(next ? "Assigned to you" : "Unassigned");
                } catch (e) { toast.error(e.response?.data?.detail || e.message); }
                finally { setAssignBusy(false); }
              }}
              data-testid="mirror-drawer-assign-btn">
              {assignBusy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                : item.assigned ? <UserCheck className="w-3 h-3 mr-1" /> : <UserPlus className="w-3 h-3 mr-1" />}
              {item.assigned || "Assign to me"}
            </Button>
          </div>
        )}

        {item && (
          <div className="px-5 py-4 space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
            <div className="space-y-4 min-w-0">
            {/* Why it's here */}
            {(item.why || []).length > 0 && (
              <div className="border-l-2 border-amber-300 bg-amber-50/50 rounded-r-md pl-3 pr-2 py-2" data-testid="mirror-drawer-why">
                <ul className="text-[11px] space-y-1 text-slate-700">
                  {item.why.includes("decline") && (
                    <li data-testid="mirror-why-decline">
                      <span className="font-semibold text-red-700">Decline ready</span> — reason:{" "}
                      <span className="italic">{cols[C.DECL_REASON] || "set"}</span>. Set Decline Send → Send to fire the decline email.
                    </li>
                  )}
                  {item.why.includes("declined") && (
                    <li data-testid="mirror-why-declined">
                      <span className="font-semibold text-slate-700">Declined</span> — reason:{" "}
                      <span className="italic">{cols[C.DECL_REASON] || "not set"}</span>. The decline is done; Autopilot moves it to Declined/Finished on its next run (or drag it there in Monday).
                    </li>
                  )}
                  {item.why.includes("offer") && (
                    <li data-testid="mirror-why-offer">
                      <span className="font-semibold text-cyan-800">Offer on file</span>
                      {cols[C.OFFER_AMT] ? <> — {fmtMoney(cols[C.OFFER_AMT])}</> : null}
                      {(item.files || []).length > 0 && <> · files attached below</>}. Set Offer Send → Send to fire the offer email.
                    </li>
                  )}
                  {item.why.includes("ir") && (
                    <li data-testid="mirror-why-ir">
                      <span className="font-semibold text-orange-800">Info was requested</span>
                      {item.ir_snippet && <> — <span className="italic">"{item.ir_snippet.slice(0, 180)}"</span></>}
                      {" "}— check the updates for the broker's reply.
                    </li>
                  )}
                </ul>
              </div>
            )}
            {/* Core details */}
            <section data-testid="mirror-drawer-core">
              <h3 className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-2">Application details</h3>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                <Field label="SF-NO"><span className="font-mono font-semibold">{cols[C.SF] || "—"}</span></Field>
                <Field label="Date of App">
                  <InlineInput type="date" value={cols[C.DATE]} onCommit={set(C.DATE, "Date of App")} testId="mirror-drawer-date" />
                </Field>
                <Field label="Status">
                  <LabelSelect value={cols[C.STATUS]} options={optFor(C.STATUS)} onCommit={set(C.STATUS, "Status")}
                    className={cn("font-medium w-full", toneFor(cols[C.STATUS]))} testId="mirror-drawer-status" />
                </Field>
                <Field label="Broker / Direct">
                  <LabelSelect value={cols[C.BD]} options={optFor(C.BD)} onCommit={set(C.BD, "Broker/Direct")}
                    className="w-full" testId="mirror-drawer-bd" />
                </Field>
                <Field label="Broker"><BrokerEdit value={cols[C.BROKER]} itemId={itemId} onChanged={load} /></Field>
                <Field label="Comms (%)">
                  <InlineInput value={cols[C.COMMS]} onCommit={set(C.COMMS, "Comms %")} testId="mirror-drawer-comms" />
                </Field>
                <Field label="OB">
                  <LabelSelect value={cols[C.OB]} options={optFor(C.OB)} onCommit={set(C.OB, "OB")}
                    className="w-full" testId="mirror-drawer-ob" />
                </Field>
                <Field label="Contact Name">
                  <InlineInput value={cols[C.CONTACT]} onCommit={set(C.CONTACT, "Contact name")} testId="mirror-drawer-contact" />
                </Field>
                <Field label="Contact Email">
                  <InlineInput value={cols[C.EMAIL]} onCommit={set(C.EMAIL, "Contact email")} testId="mirror-drawer-email" />
                </Field>
                <Field label="Customer Email">
                  <InlineInput value={cols[C.CUSTOMER_EMAIL]} onCommit={set(C.CUSTOMER_EMAIL, "Customer email")}
                    placeholder="Not on file" testId="mirror-drawer-customer-email" />
                </Field>
              </div>
            </section>
            </div>

            <div className="space-y-3 min-w-0">
            <h3 className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Decision actions</h3>
            {/* Info request */}
            <section ref={irSectionRef} className="rounded-xl shadow-sm p-3 bg-orange-50/60" data-testid="mirror-drawer-ir">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-[11px] font-semibold uppercase tracking-wide text-orange-800">Information request</h3>
                <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                  {(() => { const d = parseChaseDate(cols["long_text_mkm88qgn"]) || parseChaseDate(cols[C.LAST_IR_CHASE]); return d ? <span>last IR chase {fmtDay(d.toISOString())} ({daysSinceDate(d)}d ago)</span> : <span>never chased</span>; })()}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs mb-2">
                <Field label="Request Info">
                  <LabelSelect value={cols[C.REQ_INFO]} options={optFor(C.REQ_INFO)} onCommit={set(C.REQ_INFO, "Request Info")}
                    className="w-full" testId="mirror-drawer-reqinfo" />
                </Field>
                <Field label="IR notes (column)">
                  <InlineInput value={cols[C.IR_TEXT]} onCommit={set(C.IR_TEXT, "Information Request")} testId="mirror-drawer-irtext" />
                </Field>
              </div>
              <div className="space-y-1.5">
                <Textarea ref={irTaRef} value={irText} onChange={(e) => setIrText(e.target.value)} rows={2}
                  placeholder="What do you need from the broker? e.g. Last 3 months bank statements + HMRC position"
                  className="text-xs bg-white" data-testid="mirror-ir-textarea" />
                <Button size="sm" className="h-7 text-[11px] bg-orange-600 hover:bg-orange-700"
                  disabled={irSending || !irText.trim()} onClick={() => setIrConfirm(true)} data-testid="mirror-ir-send-btn">
                  {irSending ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
                  Send info request
                </Button>
                <p className="text-[10px] text-muted-foreground">
                  Posts an "IR:" update then flips Request Info → Request — the live automation emails {cols[C.EMAIL] || "the broker contact"} and sets status to Info Requested.
                </p>
              </div>
            </section>

            {/* Offer function */}
            <section className="rounded-xl shadow-sm p-3 bg-cyan-50/60" data-testid="mirror-drawer-offer">
              <h3 className="text-[11px] font-semibold uppercase tracking-wide text-cyan-800 mb-2">Offer function</h3>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                <Field label={`Offer Amount (${curSym()})`}>
                  <InlineInput value={cols[C.OFFER_AMT]} onCommit={set(C.OFFER_AMT, "Offer amount")} testId="mirror-drawer-offeramt" />
                </Field>
                <Field label="Offer Send">
                  <LabelSelect value={cols[C.OFFER_SEND]} options={optFor(C.OFFER_SEND)} onCommit={set(C.OFFER_SEND, "Offer Send")}
                    className="w-full" testId="mirror-drawer-offersend" />
                </Field>
                <Field label="Offer Rejected Reason">
                  <LabelSelect value={cols[C.OFFER_REJ]} options={optFor(C.OFFER_REJ)} onCommit={set(C.OFFER_REJ, "Offer rejected reason")}
                    className="w-full" testId="mirror-drawer-offerrej" />
                </Field>
                <Field label="Last offer chase"><span>{fmtDay(cols[C.LAST_OFFER_CHASE]) || "—"}</span></Field>
              </div>
            </section>

            {/* Decline function */}
            <section className="rounded-xl shadow-sm p-3 bg-red-50/60" data-testid="mirror-drawer-decline">
              <h3 className="text-[11px] font-semibold uppercase tracking-wide text-red-800 mb-2">Decline function</h3>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                <Field label="Reason for decline">
                  {customReason === null ? (
                    <div className="flex items-center gap-1">
                      <LabelSelect value={cols[C.DECL_REASON]} options={optFor(C.DECL_REASON)} onCommit={set(C.DECL_REASON, "Decline reason")}
                        className="w-full" allowEmpty testId="mirror-drawer-declreason" />
                      <button type="button" title="Write a case-specific reason (added to the Monday dropdown)"
                        onClick={(e) => { e.stopPropagation(); setCustomReason(cols[C.DECL_REASON] || ""); }}
                        className="h-6 w-6 shrink-0 inline-flex items-center justify-center rounded border bg-white text-slate-500 hover:text-red-700 hover:border-red-300"
                        data-testid="mirror-drawer-declreason-edit">
                        <Pencil className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <form className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}
                      onSubmit={async (e) => {
                        e.preventDefault();
                        const v = customReason.trim();
                        if (!v) { setCustomReason(null); return; }
                        try {
                          await mirrorSetColumn(itemId, C.DECL_REASON, v, true);
                          setItem(prev => prev ? { ...prev, cols: { ...prev.cols, [C.DECL_REASON]: v } } : prev);
                          toast.success("Decline reason saved");
                          setCustomReason(null);
                        } catch (err) { toast.error(err.response?.data?.detail || err.message); }
                      }}>
                      <input autoFocus value={customReason} onChange={(e) => setCustomReason(e.target.value)} maxLength={200}
                        placeholder="e.g. Midpoint of term 27/11/2026"
                        className="h-6 text-[11px] border rounded px-1.5 bg-white w-full" data-testid="mirror-drawer-declreason-input" />
                      <button type="submit" className="h-6 px-2 rounded bg-red-700 text-white text-[10px] shrink-0" data-testid="mirror-drawer-declreason-save">Save</button>
                      <button type="button" onClick={() => setCustomReason(null)} className="h-6 px-1.5 rounded border bg-white text-[10px] shrink-0" data-testid="mirror-drawer-declreason-cancel">Cancel</button>
                    </form>
                  )}
                </Field>
                <Field label="Decline Send">
                  <LabelSelect value={cols[C.DECL_SEND]} options={optFor(C.DECL_SEND)} onCommit={set(C.DECL_SEND, "Decline Send")}
                    className="w-full" testId="mirror-drawer-declsend" />
                </Field>
              </div>
            </section>
            </div>
            </div>

            <Tabs defaultValue="updates" data-testid="mirror-drawer-tabs"
              onValueChange={(v) => { if (v === "activity") loadActivity(); }}>
              <TabsList className="grid grid-cols-4 h-9 bg-slate-100/80">
                <TabsTrigger value="updates" className="text-[11px]" data-testid="mirror-tab-updates">Notes ({(item.updates || []).length})</TabsTrigger>
                <TabsTrigger value="files" className="text-[11px]" data-testid="mirror-tab-files">Docs ({(item.files || []).length})</TabsTrigger>
                <TabsTrigger value="activity" className="text-[11px]" data-testid="mirror-tab-activity">Activity</TabsTrigger>
                <TabsTrigger value="email" className="text-[11px]" data-testid="mirror-tab-email">Email</TabsTrigger>
              </TabsList>

            <TabsContent value="files" className="pt-3">
            <SumsubDigitalIdPanel sf={cols[C.SF] ? (/^SF-?/i.test(cols[C.SF]) ? cols[C.SF] : `SF-${cols[C.SF]}`) : ""} itemId={itemId} region={localStorage.getItem("sf_region") || "UK"} />
            {/* Files */}
            <section data-testid="mirror-drawer-files">
              <h3 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-2">
                <Paperclip className="w-3 h-3 inline mr-1" />Files ({(item.files || []).length})
              </h3>
              {(item.files || []).length > 0 && (
                <div className="border rounded divide-y max-h-40 overflow-y-auto mb-2">
                  {item.files.map(f => (
                    <a key={f.id} href={mirrorFileViewUrl(f.url, f.name, f.tok)} target="_blank" rel="noreferrer"
                      onClick={(e) => { if (e.button === 0 && !e.metaKey && !e.ctrlKey) { e.preventDefault(); e.stopPropagation(); setPreviewFile(f); } }}
                      className="flex items-center gap-2 px-2.5 py-1.5 text-[11px] hover:bg-slate-50"
                      title="Preview here (Ctrl/Cmd-click opens in a new tab)"
                      data-testid={`mirror-file-${f.id}`}>
                      <Eye className="w-3 h-3 text-slate-400 shrink-0" />
                      <span className="truncate flex-1">{f.name}</span>
                      {f.uploaded_by && (
                        <span className="text-[10px] text-violet-600 bg-violet-50 border border-violet-200 rounded px-1 shrink-0" title="Attached on an update/comment">
                          {f.uploaded_by}
                        </span>
                      )}
                      <span className="text-[10px] text-muted-foreground shrink-0 tabular-nums hidden sm:inline" title={f.created_at ? new Date(f.created_at).toLocaleString("en-GB") : ""} data-testid={`mirror-file-when-${f.id}`}>
                        {f.created_at ? new Date(f.created_at).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}
                      </span>
                      <span className="text-[10px] text-muted-foreground shrink-0">{f.size ? `${Math.round(f.size / 1024)} KB` : ""}</span>
                    </a>
                  ))}
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={(e) => { uploadFiles(e.target.files); e.target.value = ""; }}
                data-testid="mirror-file-input"
              />
              <button
                type="button"
                disabled={uploadingFiles}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); uploadFiles(e.dataTransfer?.files); }}
                className="w-full border-2 border-dashed rounded-md px-3 py-3 text-[11px] text-muted-foreground hover:border-violet-300 hover:text-violet-700 hover:bg-violet-50/50 transition-colors flex items-center justify-center gap-1.5 disabled:opacity-60"
                data-testid="mirror-file-upload-box"
              >
                {uploadingFiles ? (
                  <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading to Monday… <span className="text-slate-500 truncate" data-testid="mirror-file-upload-progress">{uploadProgress}</span></>
                ) : (
                  <><Paperclip className="w-3.5 h-3.5" /> Drop several files or a .zip here (or click to pick) — a zip is unpacked and every document lands on the Monday item</>
                )}
              </button>
            </section>
            </TabsContent>

            <TabsContent value="activity" className="pt-3">
            {/* Activity log — status/group history decoded from Monday, so you can
                see when it went to Conditional Offer, each chase stage, Offer
                Accepted etc. without leaving the drawer */}
            <section data-testid="mirror-drawer-activity">
              <h3 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-2">
                <History className="w-3 h-3 inline mr-1" />Activity log
              </h3>
              {activityRows === null || activityLoading ? (
                <div className="text-[11px] text-muted-foreground flex items-center gap-1.5 py-2">
                  <Loader2 className="w-3 h-3 animate-spin" />Loading status history…
                </div>
              ) : activityRows.length === 0 ? (
                <p className="text-[11px] text-muted-foreground py-1">No status/group changes recorded yet.</p>
              ) : (
                <div className="border rounded divide-y max-h-48 overflow-y-auto" data-testid="mirror-activity-list">
                  {activityRows.slice().reverse().map((r, i) => (
                    <div key={i} className="flex items-start gap-2 px-2.5 py-1.5 text-[11px]">
                      <span className="text-[10px] text-muted-foreground shrink-0 w-[108px]">{fmtDay(r.at)} {new Date(r.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                      <Badge variant="outline" className={cn("text-[9px] px-1 py-0 shrink-0", r.event === "status" ? "border-cyan-300 text-cyan-700" : "border-slate-300 text-slate-600")}>
                        {r.event === "status" ? "Status" : "Group"}
                      </Badge>
                      <span className="flex-1 text-slate-700">{r.detail}</span>
                      {r.by && <span className="text-[10px] text-muted-foreground shrink-0">{r.by}</span>}
                    </div>
                  ))}
                </div>
              )}
            </section>
            </TabsContent>

            <TabsContent value="email" className="pt-3">
            {/* Email chase — send from applications@ without leaving the portal */}
            <section className="rounded-xl shadow-sm p-3 bg-blue-50/50" data-testid="mirror-drawer-email-chase">
              <h3 className="text-[11px] font-semibold uppercase tracking-wide text-blue-800 mb-2">
                <Mail className="w-3 h-3 inline mr-1" />Email chase (from applications@)
              </h3>
              {!emailOpen ? (
                <Button size="sm" variant="outline" className="h-7 text-[11px] border-blue-300 text-blue-700 hover:bg-blue-100"
                  onClick={() => setEmailOpen(true)} data-testid="mirror-email-open-btn">
                  <Send className="w-3 h-3 mr-1" />Compose email…
                </Button>
              ) : (
                <div className="space-y-1.5">
                  <Input value={emailTo} onChange={(e) => setEmailTo(e.target.value)}
                    placeholder="to@…" className="h-7 text-xs bg-white" data-testid="mirror-email-to" />
                  <Input value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)}
                    placeholder="Subject" className="h-7 text-xs bg-white" data-testid="mirror-email-subject" />
                  <Textarea value={emailBody} onChange={(e) => setEmailBody(e.target.value)} rows={3}
                    placeholder="Chase message… e.g. Hi, any update on the requested bank statements?"
                    className="text-xs bg-white" data-testid="mirror-email-body" />
                  <div className="flex gap-2">
                    <Button size="sm" className="h-7 text-[11px] bg-blue-600 hover:bg-blue-700"
                      disabled={emailSending || !emailTo.includes("@") || !emailBody.trim() || !emailSubject.trim()}
                      onClick={sendChaseEmail} data-testid="mirror-email-send-btn">
                      {emailSending ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
                      Send email
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 text-[11px]"
                      onClick={() => setEmailOpen(false)} data-testid="mirror-email-cancel-btn">Cancel</Button>
                  </div>
                  <p className="text-[10px] text-muted-foreground">
                    Sends from the applications mailbox and logs a copy as a Monday update.
                  </p>
                </div>
              )}
            </section>
            </TabsContent>

            <TabsContent value="updates" className="pt-3">
            {/* Updates */}
            <section data-testid="mirror-drawer-updates">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  <MessageSquare className="w-3 h-3 inline mr-1" />Updates ({(item.updates || []).length})
                </h3>
                <div className="flex items-center gap-1" data-testid="mirror-updates-filter">
                  {[["all", "All"], ["team", "Needs action"], ["system", "Automated"]].map(([v, label]) => (
                    <button key={v} type="button" onClick={() => setUpdatesFilter(v)}
                      className={cn("text-[10px] px-1.5 py-0.5 rounded border", updatesFilter === v
                        ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400")}
                      data-testid={`mirror-updates-filter-${v}`}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-1.5 mb-2">
                <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
                  placeholder="Post an update to this app's Monday file…" className="text-xs" data-testid="mirror-note-textarea" />
                <Button size="sm" className="h-7 text-[11px]" disabled={posting || !note.trim()} onClick={postNote}
                  data-testid="mirror-note-post-btn">
                  {posting ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
                  Post update
                </Button>
              </div>
              <div className="space-y-2">
                {(item.updates || [])
                  .filter((u) => updatesFilter === "all" || (updatesFilter === "system") === !!u.is_system)
                  .map((u, ui) => {
                  const isRecent = u.created_at && (Date.now() - new Date(u.created_at).getTime()) < 48 * 3600 * 1000;
                  return (
                  <div key={u.id}
                    className={cn("border rounded p-2",
                      ui === 0 && isRecent ? "bg-amber-50 border-amber-300 ring-1 ring-amber-200"
                        : isRecent ? "bg-amber-50/50 border-amber-200" : "bg-slate-50/60")}
                    data-testid={`mirror-update-${u.id}`}>
                    <div className="flex items-center gap-2 text-[10px] text-muted-foreground mb-1">
                      {u.is_system ? (
                        <span className="flex items-center gap-1 text-slate-500" title="Posted by an automated job, not a team member">
                          <Bot className="w-3 h-3" /><span className="font-semibold">{u.shown_by || "System"}</span>
                        </span>
                      ) : (
                        <span className="font-semibold text-slate-700" data-testid={`mirror-update-${u.id}-author`}>{u.shown_by || "—"}</span>
                      )}
                      <span>{u.created_at ? new Date(u.created_at).toLocaleString("en-GB") : ""}</span>
                      {isRecent && (
                        <span className="text-[9px] font-bold text-amber-700 bg-amber-100 border border-amber-300 rounded px-1"
                          data-testid={`mirror-update-new-${u.id}`}>NEW</span>
                      )}
                    </div>
                    <div className="text-[11px] whitespace-pre-wrap break-words max-h-40 overflow-y-auto">{u.body}</div>
                    <MirrorFileChips files={u.files} onOpen={setPreviewFile} />
                    {(u.replies || []).length > 0 && (
                      <div className="mt-1.5 ml-3 pl-2 border-l-2 border-slate-200 space-y-1.5" data-testid={`mirror-update-${u.id}-replies`}>
                        {u.replies.map(r => (
                          <div key={r.id} data-testid={`mirror-reply-${r.id}`}>
                            <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                              <span className="font-semibold text-slate-600">{r.creator || "—"}</span>
                              <span>{r.created_at ? new Date(r.created_at).toLocaleString("en-GB") : ""}</span>
                              {(r.files || []).length > 0 && <span className="text-[9px] text-violet-600" data-testid={`mirror-reply-${r.id}-filecount`}>· {r.files.length} file{r.files.length === 1 ? "" : "s"}</span>}
                            </div>
                            <div className="text-[11px] whitespace-pre-wrap break-words">{r.body}</div>
                            <MirrorFileChips files={r.files} onOpen={setPreviewFile} testPrefix="mirror-reply-file" />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  );
                })}
                {(item.updates || []).length === 0 && (
                  <div className="text-xs text-muted-foreground text-center py-3">No updates yet.</div>
                )}
              </div>
            </section>
            </TabsContent>
            </Tabs>
            {previewFile && <MirrorFilePreview file={previewFile} onClose={() => setPreviewFile(null)} />}
          </div>
        )}

        {actionConfirm && (
          <AlertDialog open onOpenChange={(o) => !o && setActionConfirm(null)}>
            <AlertDialogContent data-testid="mirror-action-confirm">
              <AlertDialogHeader>
                <AlertDialogTitle className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-amber-500" />
                  {actionConfirm === "offer" ? "Send the offer?" : "Send the decline?"}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {actionConfirm === "offer"
                    ? `This fires the live offer automation — the offer email${fmtMoney(cols[C.OFFER_AMT]) ? ` (${fmtMoney(cols[C.OFFER_AMT])})` : ""} goes out to ${cols[C.EMAIL] || "the broker/customer"} via Make.`
                    : `This fires the live decline automation — the decline email is sent and the app moves to Declined. Reason: ${cols[C.DECL_REASON] || "—"}.`}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="mirror-action-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction
                  className={actionConfirm === "offer" ? "bg-cyan-600 hover:bg-cyan-700" : "bg-red-600 hover:bg-red-700"}
                  onClick={fireAction} data-testid="mirror-action-confirm-btn">
                  Yes — send it
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}

        {irConfirm && (
          <AlertDialog open onOpenChange={(o) => !o && setIrConfirm(false)}>
            <AlertDialogContent data-testid="mirror-ir-confirm">
              <AlertDialogHeader>
                <AlertDialogTitle className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-amber-500" />Send this info request?
                </AlertDialogTitle>
                <AlertDialogDescription>
                  An email goes out to {cols[C.EMAIL] || "the broker contact"} right away and the app's status moves to Info Requested.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="mirror-ir-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction className="bg-orange-600 hover:bg-orange-700" onClick={sendInfoRequest}
                  data-testid="mirror-ir-confirm-btn">
                  Send it
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Field({ label, children }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] text-muted-foreground mb-0.5">{label}</div>
      {children}
    </div>
  );
}

// ── chase dialogs ────────────────────────────────────────────────────────────
// Final offer chase — the app will move to Offer Rejected, so the
// OfferRejectedReason column must be set before the chase can fire.
function FinalChaseReasonDialog({ item, options, onClose, onConfirm }) {
  const [reason, setReason] = useState("");
  return (
    <AlertDialog open onOpenChange={(o) => !o && onClose()}>
      <AlertDialogContent className="max-w-md" data-testid="final-chase-reason-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-red-700">Final Offer Chase — reason required</AlertDialogTitle>
          <AlertDialogDescription>
            This chase moves <span className="font-semibold text-slate-700">{item.name}</span> to{" "}
            <span className="font-semibold text-slate-700">Offer Rejected</span>. An OfferRejectedReason
            must be recorded before the final chase can fire.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div>
          <label className="text-xs text-muted-foreground block mb-1">OfferRejectedReason *</label>
          <select value={reason} onChange={(e) => setReason(e.target.value)}
            className="w-full h-9 text-sm border rounded-md px-2 bg-white"
            data-testid="final-chase-reason-select">
            <option value="">Pick a reason…</option>
            {options.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onClose}>Cancel</AlertDialogCancel>
          <AlertDialogAction className="bg-red-600 hover:bg-red-700" disabled={!reason}
            onClick={() => onConfirm(reason)} data-testid="final-chase-fire-btn">
            <Zap className="w-3.5 h-3.5 mr-1" /> Set Reason &amp; Fire Final Chase
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function ChaseConfirmDialog({ item, cfg, onClose, onConfirm }) {
  const ci = chaseInfo(item, 0, cfg);
  const cols = item.cols || {};
  return (
    <AlertDialog open onOpenChange={(o) => !o && onClose()}>
      <AlertDialogContent data-testid="mirror-chase-confirm">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-amber-500" />Chase {item.name}?
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-1.5">
              <p>
                Current stage: <b>{cols[C.STATUS]}</b> → fires: <b>{ci.stage?.what}</b>
                {!ci.isTopUp && cols[C.EMAIL] ? <> ({cols[C.EMAIL]})</> : null}.
              </p>
              {ci.isTopUp && <p className="text-purple-700">Top-up app — the chase routes to Dean + the customer contact.</p>}
              {ci.stage?.danger && (
                <p className="text-red-600 font-medium">⚠ This is the final stage — the app moves to {(ci.stage.next || "").toUpperCase()} after this chase.</p>
              )}
              {ci.lastDate
                ? <p className="text-muted-foreground">Last chased {ci.days} day{ci.days === 1 ? "" : "s"} ago.</p>
                : <p className="text-muted-foreground">Never chased before.</p>}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="mirror-chase-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction className={ci.stage?.danger ? "bg-red-600 hover:bg-red-700" : "bg-orange-600 hover:bg-orange-700"}
            onClick={onConfirm} data-testid="mirror-chase-confirm-btn">
            Yes — chase it
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function ChaseAllDialog({ items, threshold, cfg, onClose, fireChase, onDone }) {
  const [ticked, setTicked] = useState(() => new Set(
    items.filter(i => !chaseInfo(i, threshold, cfg).stage?.danger).map(i => i.id)));
  const [running, setRunning] = useState(false);
  const [finished, setFinished] = useState(false);
  const [progress, setProgress] = useState({});

  const run = async () => {
    setRunning(true);
    for (const it of items) {
      if (!ticked.has(it.id)) continue;
      setProgress(p => ({ ...p, [it.id]: "running" }));
      const ok = await fireChase(it);
      setProgress(p => ({ ...p, [it.id]: ok ? "done" : "failed" }));
    }
    setRunning(false);
    setFinished(true);
  };

  return (
    <AlertDialog open onOpenChange={(o) => { if (!o && !running) (finished ? onDone() : onClose()); }}>
      <AlertDialogContent className="max-w-lg" data-testid="mirror-chase-all-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-amber-500" />Chase all due ({items.length})
          </AlertDialogTitle>
          <AlertDialogDescription>
            Each tick fires the live chase automation — one email per app, stage-appropriate.
            Final-stage apps (would be declined / offer-rejected) are unticked by default.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="max-h-64 overflow-y-auto border rounded divide-y">
          {items.map(it => {
            const ci = chaseInfo(it, threshold, cfg);
            const st = progress[it.id];
            return (
              <label key={it.id} className="flex items-center gap-2 px-2.5 py-1.5 text-[11px] cursor-pointer hover:bg-slate-50"
                data-testid={`mirror-chase-all-row-${it.id}`}>
                <input type="checkbox" checked={ticked.has(it.id)} disabled={running || finished}
                  onChange={() => setTicked(prev => { const n = new Set(prev); n.has(it.id) ? n.delete(it.id) : n.add(it.id); return n; })} />
                <span className="flex-1 truncate font-medium">{it.name}</span>
                <span className={cn("text-[10px]", ci.stage?.danger ? "text-red-600 font-semibold" : "text-muted-foreground")}>
                  {(it.cols || {})[C.STATUS]}{ci.stage?.danger ? ` → ${(ci.stage.next || "").toUpperCase()}` : ""}
                </span>
                <span className="text-[10px] text-muted-foreground w-14 text-right">
                  {ci.lastDate ? `${ci.days}d ago` : "never"}
                </span>
                <span className="w-4 text-center">
                  {st === "running" && <Loader2 className="w-3 h-3 animate-spin inline text-orange-500" />}
                  {st === "done" && <span className="text-green-600">✓</span>}
                  {st === "failed" && <span className="text-red-600">✗</span>}
                </span>
              </label>
            );
          })}
        </div>
        <AlertDialogFooter>
          {!finished ? (
            <>
              <AlertDialogCancel disabled={running} onClick={onClose} data-testid="mirror-chase-all-cancel">Cancel</AlertDialogCancel>
              <Button className="bg-orange-600 hover:bg-orange-700" disabled={running || ticked.size === 0}
                onClick={run} data-testid="mirror-chase-all-run-btn">
                {running ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Zap className="w-3.5 h-3.5 mr-1" />}
                Fire {ticked.size} chase{ticked.size === 1 ? "" : "s"}
              </Button>
            </>
          ) : (
            <Button onClick={onDone} data-testid="mirror-chase-all-done-btn">Close</Button>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
